import { useEffect, useState, useCallback } from 'react';
import { getEntries, clearEntries, subscribe, formatEntries, BUILD_TIME } from '../debug/errorLog';
import styles from './DebugOverlay.module.css';

/**
 * Hidden crash-log viewer for the deployed app.
 *
 * - Opens with `?debug=1` in the URL, or by pressing Ctrl/⌘+Shift+D.
 * - When closed but errors exist, a small red badge appears (bottom-left) so an
 *   unattended screen still signals "something broke — tap here".
 * - Copy button puts the full log (with build id + state snapshots) on the
 *   clipboard so it can be pasted into a bug report.
 */
export default function DebugOverlay() {
  const forceOpen = typeof location !== 'undefined' && new URLSearchParams(location.search).get('debug') === '1';
  const [open, setOpen] = useState(forceOpen);
  const [, bump] = useState(0);
  const [copied, setCopied] = useState(false);

  // Re-render on new log entries
  useEffect(() => subscribe(() => bump((n) => n + 1)), []);

  // Toggle with Ctrl/⌘ + Shift + D
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && (e.key === 'D' || e.key === 'd')) {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const entries = getEntries();
  const errorCount = entries.length;

  const copy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(formatEntries());
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard blocked — user can still read it */ }
  }, []);

  if (!open) {
    if (errorCount === 0) return null;
    return (
      <button className={styles.badge} onClick={() => setOpen(true)} title="Errors captured — open crash log">
        ⚠ {errorCount}
      </button>
    );
  }

  return (
    <div className={styles.panel}>
      <div className={styles.head}>
        <span className={styles.title}>Crash Log · {errorCount}</span>
        <span className={styles.build}>build {String(BUILD_TIME).replace('T', ' ').slice(0, 16)}</span>
        <button className={styles.btn} onClick={copy}>{copied ? 'Copied ✓' : 'Copy'}</button>
        <button className={styles.btn} onClick={() => clearEntries()}>Clear</button>
        <button className={styles.btn} onClick={() => setOpen(false)}>✕</button>
      </div>
      <div className={styles.body}>
        {entries.length === 0 && <div className={styles.empty}>No errors captured.</div>}
        {entries.map((e, i) => (
          <div key={i} className={styles.entry}>
            <div className={styles.entryHead}>
              <span className={styles.type}>{e.type}</span>
              <span className={styles.time}>{new Date(e.t).toLocaleTimeString()}</span>
              {e.state?.scenario && <span className={styles.tag}>{e.state.scenario}</span>}
              {e.state?.rhythm && <span className={styles.tag}>{e.state.rhythm}</span>}
            </div>
            <div className={styles.msg}>{e.message}</div>
            {(e.stack || e.componentStack) && (
              <pre className={styles.stack}>{(e.componentStack || e.stack).split('\n').slice(0, 6).join('\n')}</pre>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
